import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  X, 
  Users, 
  Stethoscope, 
  ClipboardList, 
  Pill, 
  Briefcase,
  Loader2,
  ArrowRight,
  FileText,
  Hash,
  Phone,
  Mail,
  Tag,
  Package,
  ChevronRight,
  Sparkles,
  Command
} from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const categoryIcons = {
  patients: Users,
  specialists: Stethoscope,
  orders: ClipboardList,
  medications: Pill,
  services: Briefcase
};

// Status color mapping
const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  received: 'bg-blue-100 text-blue-700 border-blue-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-600 border-red-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  urgent: 'bg-red-100 text-red-700 border-red-200'
};

// Result card components for each category
const PatientResultCard = ({ item, onClick }) => (
  <div 
    onClick={onClick}
    className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-[#E63946]/30 hover:shadow-lg hover:shadow-[#E63946]/5 cursor-pointer transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
          {item.first_name?.[0]}{item.last_name?.[0]}
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors">
            {item.first_name} {item.last_name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {item.document_type}: {item.document_number}
            </span>
          </div>
        </div>
      </div>
      <Badge variant="outline" className={`text-xs ${statusColors[item.status] || statusColors.active}`}>
        {item.status === 'active' ? 'Activo' : 'Inactivo'}
      </Badge>
    </div>
    {(item.phone || item.email) && (
      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-4 text-xs text-gray-500">
        {item.phone && (
          <span className="flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {item.phone}
          </span>
        )}
        {item.email && (
          <span className="flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {item.email}
          </span>
        )}
      </div>
    )}
  </div>
);

const SpecialistResultCard = ({ item, onClick }) => (
  <div 
    onClick={onClick}
    className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-[#E63946]/30 hover:shadow-lg hover:shadow-[#E63946]/5 cursor-pointer transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-semibold text-sm">
          {item.first_name?.[0]}{item.last_name?.[0]}
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors">
            {item.first_name} {item.last_name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <Stethoscope className="w-3 h-3" />
            {item.specialty || 'Especialista'}
          </div>
        </div>
      </div>
      <Badge variant="outline" className={`text-xs ${statusColors[item.status] || statusColors.active}`}>
        {item.status === 'active' ? 'Activo' : 'Inactivo'}
      </Badge>
    </div>
  </div>
);

const OrderResultCard = ({ item, onClick }) => (
  <div 
    onClick={onClick}
    className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-[#E63946]/30 hover:shadow-lg hover:shadow-[#E63946]/5 cursor-pointer transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white">
          <ClipboardList className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors">
            {item.patient_name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <Hash className="w-3 h-3" />
            {item.id?.slice(0, 8)}...
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Badge variant="outline" className={`text-xs ${statusColors[item.status] || 'bg-gray-100 text-gray-600'}`}>
          {item.status}
        </Badge>
        <Badge variant="outline" className={`text-xs ${statusColors[item.priority] || 'bg-blue-100 text-blue-600'}`}>
          {item.priority}
        </Badge>
      </div>
    </div>
  </div>
);

const MedicationResultCard = ({ item, onClick }) => (
  <div 
    onClick={onClick}
    className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-[#E63946]/30 hover:shadow-lg hover:shadow-[#E63946]/5 cursor-pointer transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center text-white">
          <Pill className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors">
            {item.generic_name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            {item.commercial_name && (
              <span className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {item.commercial_name}
              </span>
            )}
            {item.presentation && (
              <span>• {item.presentation}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Badge variant="outline" className={`text-xs ${statusColors[item.status] || statusColors.active}`}>
          {item.status === 'active' ? 'Activo' : 'Inactivo'}
        </Badge>
        {item.stock_quantity !== undefined && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Package className="w-3 h-3" />
            {item.stock_quantity} uds
          </span>
        )}
      </div>
    </div>
  </div>
);

const ServiceResultCard = ({ item, onClick }) => (
  <div 
    onClick={onClick}
    className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-[#E63946]/30 hover:shadow-lg hover:shadow-[#E63946]/5 cursor-pointer transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white">
          <Briefcase className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors">
            {item.name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <span className="flex items-center gap-1">
              <Hash className="w-3 h-3" />
              {item.code}
            </span>
            {item.category && (
              <span>• {item.category}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Badge variant="outline" className={`text-xs ${statusColors[item.status] || statusColors.active}`}>
          {item.status === 'active' ? 'Activo' : 'Inactivo'}
        </Badge>
        {item.price && (
          <span className="text-xs font-medium text-gray-700">
            ${item.price.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  </div>
);

// Map category to result card component
const resultCardComponents = {
  patients: PatientResultCard,
  specialists: SpecialistResultCard,
  orders: OrderResultCard,
  medications: MedicationResultCard,
  services: ServiceResultCard
};

export const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  const searchTimeoutRef = useRef(null);

  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/search`, {
        params: { q: searchQuery, limit: 5 }
      });
      setResults(response.data);
      
      // Auto-select first category with results
      const categories = Object.keys(response.data.categories || {});
      const firstWithResults = categories.find(
        cat => response.data.categories[cat]?.items?.length > 0
      );
      setActiveCategory(firstWithResults || null);
    } catch (error) {
      console.error('Search error:', error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults(null);
      setActiveCategory(null);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleResultClick = (category, item) => {
    const paths = {
      patients: `/patients`,
      specialists: `/specialists`,
      orders: `/orders`,
      medications: `/medications`,
      services: `/services`
    };
    navigate(paths[category] || '/dashboard');
    onClose();
  };

  const handleViewAll = (category, path) => {
    navigate(`${path}?search=${encodeURIComponent(query)}`);
    onClose();
  };

  const categoriesWithResults = results?.categories 
    ? Object.entries(results.categories).filter(([_, cat]) => cat.items?.length > 0)
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideCloseButton className="max-w-4xl p-0 gap-0 bg-gray-50/95 backdrop-blur-xl border-gray-200/50 shadow-2xl overflow-hidden">
        {/* Search Header */}
        <div className="relative border-b border-gray-200 bg-white">
          <div className="flex items-center px-4">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Buscar pacientes, especialistas, órdenes, medicamentos, servicios..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 h-14 border-0 bg-transparent text-base placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="global-search-input"
            />
            {loading && <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
            {query && !loading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuery('')}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Results Area */}
        <div className="flex min-h-[400px] max-h-[70vh]">
          {/* No query state */}
          {!query && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E63946] to-[#C92A36] flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Búsqueda Global
              </h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Escribe al menos 2 caracteres para buscar en pacientes, especialistas, órdenes, medicamentos y servicios.
              </p>
              <div className="mt-6 flex items-center gap-2 text-xs text-gray-400">
                <kbd className="px-2 py-1 bg-gray-100 rounded text-gray-600 font-mono">⌘</kbd>
                <span>+</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-gray-600 font-mono">K</kbd>
                <span className="ml-2">para abrir búsqueda</span>
              </div>
            </div>
          )}

          {/* Loading state */}
          {query && loading && !results && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-[#E63946] animate-spin" />
                <span className="text-sm text-gray-500">Buscando...</span>
              </div>
            </div>
          )}

          {/* No results state */}
          {query && !loading && results?.total_count === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Sin resultados
              </h3>
              <p className="text-sm text-gray-500 max-w-sm">
                No se encontraron resultados para "<span className="font-medium">{query}</span>". 
                Intenta con otros términos de búsqueda.
              </p>
            </div>
          )}

          {/* Results */}
          {query && !loading && categoriesWithResults.length > 0 && (
            <>
              {/* Category Sidebar */}
              <div className="w-56 border-r border-gray-200 bg-white p-2 flex-shrink-0">
                <div className="space-y-1">
                  {categoriesWithResults.map(([key, category]) => {
                    const Icon = categoryIcons[key] || FileText;
                    const isActive = activeCategory === key;
                    
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveCategory(key)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all duration-150 ${
                          isActive 
                            ? 'bg-[#E63946]/10 text-[#E63946]' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid={`search-category-${key}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon className={`w-4 h-4 ${isActive ? 'text-[#E63946]' : 'text-gray-500'}`} />
                          <span className="font-medium text-sm">{category.label}</span>
                        </div>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${
                            isActive 
                              ? 'bg-[#E63946] text-white' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {category.count}
                        </Badge>
                      </button>
                    );
                  })}
                </div>

                {/* Total results */}
                <div className="mt-4 pt-4 border-t border-gray-100 px-3">
                  <div className="text-xs text-gray-500">
                    Total: <span className="font-semibold text-gray-900">{results.total_count}</span> resultados
                  </div>
                </div>
              </div>

              {/* Results Content */}
              <div className="flex-1 bg-gray-50">
                <ScrollArea className="h-[70vh]">
                  <div className="p-4">
                    {activeCategory && results.categories[activeCategory] && (
                      <>
                        {/* Category Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const Icon = categoryIcons[activeCategory] || FileText;
                              return <Icon className="w-5 h-5 text-[#E63946]" />;
                            })()}
                            <h3 className="font-semibold text-gray-900">
                              {results.categories[activeCategory].label}
                            </h3>
                            <Badge variant="secondary" className="text-xs">
                              {results.categories[activeCategory].count} encontrados
                            </Badge>
                          </div>
                          {results.categories[activeCategory].count > 5 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAll(activeCategory, results.categories[activeCategory].path)}
                              className="text-[#E63946] hover:text-[#C92A36] hover:bg-[#E63946]/10"
                            >
                              Ver todos
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>

                        {/* Result Cards */}
                        <div className="space-y-3">
                          {results.categories[activeCategory].items.map((item, index) => {
                            const ResultCard = resultCardComponents[activeCategory];
                            return ResultCard ? (
                              <ResultCard
                                key={item.id || index}
                                item={item}
                                onClick={() => handleResultClick(activeCategory, item)}
                              />
                            ) : null;
                          })}
                        </div>

                        {/* View all link */}
                        {results.categories[activeCategory].count > 5 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <Button
                              variant="outline"
                              className="w-full justify-center text-[#E63946] border-[#E63946]/30 hover:bg-[#E63946]/10"
                              onClick={() => handleViewAll(activeCategory, results.categories[activeCategory].path)}
                            >
                              Ver los {results.categories[activeCategory].count} resultados
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white px-4 py-2.5 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-[10px]">↵</kbd>
              seleccionar
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-[10px]">esc</kbd>
              cerrar
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Command className="w-3 h-3" />
            Búsqueda Global
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
